import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Plus, UserCircle, Edit, Trash2, Phone, Mail, MessageSquare, Download, Star, Zap } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactsManagement = ({ getAuthHeaders, customerId = null, customerName = null, layout = "cards", maxHeight = null, onContactAdded = null, onQuickQuote = null, onQuickCommLog = null }) => {
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: customerId || '',
    company_id: '',
    first_name: '',
    last_name: '',
    alias: '',
    email: '',
    phone: '',
    role: '',
    preferred_contact_method: 'email',
    is_primary: false,
    marketing_opt_in_email: true,
    marketing_opt_in_sms: true,
    marketing_opt_in_whatsapp: false,
    notes: ''
  });

  useEffect(() => {
    fetchContacts();
  }, [customerId]);

  const fetchContacts = async () => {
    try {
      const url = customerId
        ? `${API}/contacts?customer_id=${customerId}`
        : `${API}/contacts`;
      const response = await axios.get(url, getAuthHeaders());
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to fetch contacts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clean up form data - remove empty strings and replace with null
      const submitData = Object.keys(formData).reduce((acc, key) => {
        const value = formData[key];
        
        // Always include customer_id if we have it
        if (key === 'customer_id' && customerId) {
          acc[key] = customerId;
          return acc;
        }
        
        // Skip company_id as it will be set by backend
        if (key === 'company_id') return acc;
        
        // Handle different data types
        if (value === '' || value === null || value === undefined) {
          // Only include non-empty values, let backend use defaults
          return acc;
        } else if (typeof value === 'boolean' || typeof value === 'number') {
          acc[key] = value;
        } else {
          acc[key] = value;
        }
        
        return acc;
      }, {});

      console.log('Submitting contact data:', submitData);

      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, submitData, getAuthHeaders());
        toast.success('Contact updated successfully');
      } else {
        await axios.post(`${API}/contacts`, submitData, getAuthHeaders());
        toast.success('Contact created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchContacts();
      
      // Notify parent component to refresh its contacts list
      if (onContactAdded) {
        onContactAdded();
      }
    } catch (error) {
      console.error('Contact operation error:', error);
      
      let errorMessage = 'Operation failed';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          if (Array.isArray(error.response.data.detail)) {
            // Handle Pydantic validation errors
            errorMessage = error.response.data.detail
              .map(err => `${err.loc ? err.loc.join(' ') : 'Field'}: ${err.msg}`)
              .join(', ');
          } else {
            errorMessage = error.response.data.detail;
          }
        } else if (Array.isArray(error.response.data)) {
          // Handle direct validation error array
          errorMessage = error.response.data
            .map(err => `${err.loc ? err.loc.join(' ') : 'Field'}: ${err.msg}`)
            .join(', ');
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await axios.delete(`${API}/contacts/${id}`, getAuthHeaders());
        toast.success('Contact deleted successfully');
        fetchContacts();
      } catch (error) {
        toast.error('Failed to delete contact');
      }
    }
  };

  const handleExport = async (id, name) => {
    try {
      const response = await axios.get(`${API}/contacts/${id}/export`, getAuthHeaders());
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contact-${name.replace(/\s+/g, '-')}-export.json`;
      link.click();
      toast.success('Contact data exported');
    } catch (error) {
      toast.error('Failed to export contact data');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: customerId || '',
      company_id: '',
      first_name: '',
      last_name: '',
      alias: '',
      email: '',
      phone: '',
      role: '',
      preferred_contact_method: 'email',
      is_primary: false,
      marketing_opt_in_email: true,
      marketing_opt_in_sms: true,
      marketing_opt_in_whatsapp: false,
      notes: ''
    });
    setEditingContact(null);
  };

  const openEditDialog = (contact) => {
    setEditingContact(contact);
    setFormData({
      customer_id: contact.customer_id,
      company_id: contact.company_id,
      first_name: contact.first_name,
      last_name: contact.last_name,
      alias: contact.alias || '',
      email: contact.email || '',
      phone: contact.phone || '',
      role: contact.role || '',
      preferred_contact_method: contact.preferred_contact_method || 'email',
      is_primary: contact.is_primary,
      marketing_opt_in_email: contact.marketing_opt_in_email ?? true,
      marketing_opt_in_sms: contact.marketing_opt_in_sms ?? true,
      marketing_opt_in_whatsapp: contact.marketing_opt_in_whatsapp ?? false,
      notes: contact.notes || ''
    });
    setIsDialogOpen(true);
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'phone': return <Phone className="w-4 h-4" />;
      case 'sms': return <MessageSquare className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'whatsapp': return <MessageSquare className="w-4 h-4" />;
      default: return <Mail className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {!customerId && (
        <div>
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>Contacts</h2>
          <p className="text-gray-500">Manage customer contacts and communication preferences</p>
        </div>
      )}

      <div className="flex justify-between items-center">
        {customerName && <h3 className="text-xl font-bold">Contacts for {customerName}</h3>}
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-gradient-to-r from-blue-600 to-purple-600" data-testid="create-contact-button">
              <Plus className="w-4 h-4" />
              Add Contact
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingContact ? 'Edit Contact' : 'Create Contact'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    data-testid="contact-first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    data-testid="contact-last-name-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="alias">Alias/Nickname</Label>
                  <Input
                    id="alias"
                    value={formData.alias}
                    onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                    placeholder="e.g., Bill for William"
                    data-testid="contact-alias-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Input
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    placeholder="e.g., Fleet Manager, Accounts"
                    data-testid="contact-role-input"
                  />
                </div>
                <div>
                  <Label htmlFor="preferred_method">Preferred Contact Method</Label>
                  <Select
                    value={formData.preferred_contact_method}
                    onValueChange={(value) => setFormData({ ...formData, preferred_contact_method: value })}
                  >
                    <SelectTrigger data-testid="contact-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Marketing Preferences (GDPR)</Label>
                <div className="space-y-2 mt-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.marketing_opt_in_email}
                      onChange={(e) => setFormData({ ...formData, marketing_opt_in_email: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Email Marketing</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.marketing_opt_in_sms}
                      onChange={(e) => setFormData({ ...formData, marketing_opt_in_sms: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">SMS Marketing</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.marketing_opt_in_whatsapp}
                      onChange={(e) => setFormData({ ...formData, marketing_opt_in_whatsapp: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">WhatsApp Marketing</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_primary}
                    onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm font-medium">Primary Contact</span>
                </label>
              </div>

              <div>
                <Label htmlFor="notes">Notes</Label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full min-h-20 px-3 py-2 border rounded-md"
                  placeholder="Internal notes about this contact..."
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="flex-1" data-testid="contact-submit-button">
                  {editingContact ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="text-center py-12">Loading...</div>
      ) : contacts.length === 0 ? (
        <Card className="p-12 text-center">
          <UserCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No contacts yet. Add your first contact!</p>
        </Card>
      ) : layout === "table" ? (
        <div className={`rounded-md border ${maxHeight ? `overflow-auto ${maxHeight}` : ''}`}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Contact Details</TableHead>
                <TableHead>Preferred Method</TableHead>
                <TableHead>Marketing Preferences</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <UserCircle className="w-4 h-4 text-blue-600" />
                      <div>
                        <div className="flex items-center gap-1">
                          <span className="font-medium" data-testid={`contact-name-${contact.id}`}>
                            {contact.first_name} {contact.last_name}
                          </span>
                          {contact.is_primary && (
                            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" title="Primary Contact" />
                          )}
                        </div>
                        {contact.alias && (
                          <p className="text-xs text-gray-500">"{contact.alias}"</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {contact.role && (
                      <Badge variant="outline" className="text-xs">{contact.role}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1 text-sm">
                      {contact.email && (
                        <div className="flex items-center gap-1">
                          <Mail className="w-3 h-3 text-gray-400" />
                          <span>{contact.email}</span>
                        </div>
                      )}
                      {contact.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3 text-gray-400" />
                          <span>{contact.phone}</span>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {contact.preferred_contact_method && (
                      <div className="flex items-center gap-1 text-sm">
                        {getMethodIcon(contact.preferred_contact_method)}
                        <span className="capitalize">{contact.preferred_contact_method}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {contact.marketing_opt_in_email && (
                        <Badge variant="secondary" className="text-xs">Email</Badge>
                      )}
                      {contact.marketing_opt_in_sms && (
                        <Badge variant="secondary" className="text-xs">SMS</Badge>
                      )}
                      {contact.marketing_opt_in_whatsapp && (
                        <Badge variant="secondary" className="text-xs">WhatsApp</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {onQuickQuote && (
                        <Button 
                          size="sm" 
                          variant="default"
                          className="bg-gradient-to-r from-blue-600 to-indigo-600"
                          onClick={() => onQuickQuote(contact)}
                          title="Quick Quote for this contact"
                        >
                          <Zap className="w-3 h-3" />
                        </Button>
                      )}
                      {onQuickCommLog && (
                        <Button 
                          size="sm" 
                          variant="default"
                          className="bg-gradient-to-r from-green-600 to-emerald-600"
                          onClick={() => onQuickCommLog(contact)}
                          title="Quick Log Communication - Cursor ready in notes"
                        >
                          <MessageSquare className="w-3 h-3" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEditDialog(contact)}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleExport(contact.id, `${contact.first_name}-${contact.last_name}`)}>
                        <Download className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(contact.id)} className="text-red-600 hover:text-red-700">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contacts.map((contact) => (
            <Card key={contact.id} className="p-6 hover:shadow-lg transition-shadow" data-testid={`contact-card-${contact.id}`}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <UserCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold" data-testid={`contact-name-${contact.id}`}>
                        {contact.first_name} {contact.last_name}
                      </h3>
                      {contact.is_primary && (
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" title="Primary Contact" />
                      )}
                    </div>
                    {contact.alias && (
                      <p className="text-xs text-gray-500">aka "{contact.alias}"</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-600 mb-4">
                {contact.role && (
                  <Badge variant="outline" className="text-xs">{contact.role}</Badge>
                )}
                {contact.email && <p>{contact.email}</p>}
                {contact.phone && <p>{contact.phone}</p>}
                {contact.preferred_contact_method && (
                  <div className="flex items-center gap-2 mt-2">
                    {getMethodIcon(contact.preferred_contact_method)}
                    <span className="text-xs">Prefers {contact.preferred_contact_method}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEditDialog(contact)} className="flex-1">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleExport(contact.id, `${contact.first_name}-${contact.last_name}`)}>
                  <Download className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDelete(contact.id)} className="text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactsManagement;
